import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, StoryListItem } from '../services/adminApi'
import styles from './StoriesPage.module.css'

export default function StoriesPage() {
  const { apiKey } = useAdmin()

  const [stories, setStories] = useState<StoryListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<StoryListItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [exporting, setExporting] = useState<Set<string>>(new Set())
  const [exportErrors, setExportErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const api = createAdminApi(() => apiKey)
    setLoading(true)
    setError('')
    api.getStories()
      .then(setStories)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey])

  async function handleExport(filename: string) {
    if (!filename) return
    const api = createAdminApi(() => apiKey)
    setExporting(prev => new Set(prev).add(filename))
    setExportErrors(prev => { const next = { ...prev }; delete next[filename]; return next })
    try {
      await api.exportStory(filename)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setExportErrors(prev => ({ ...prev, [filename]: msg }))
    } finally {
      setExporting(prev => { const next = new Set(prev); next.delete(filename); return next })
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return
    const api = createAdminApi(() => apiKey)
    setDeleting(true)
    setDeleteError('')
    try {
      await api.deleteStory(confirmDelete.id)
      setStories(prev => prev.filter(s => s.id !== confirmDelete.id))
      setConfirmDelete(null)
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Stories</h1>
        {!loading && (
          <span className={styles.headerCount}>{stories.length} {stories.length === 1 ? 'story' : 'stories'}</span>
        )}
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.content}>
        {loading ? (
          <p className={styles.empty}>Loading…</p>
        ) : stories.length === 0 ? (
          <p className={styles.empty}>No stories found.</p>
        ) : (
          <div className={styles.grid}>
            {stories.map(story => (
              <div
                key={story.sourceFilename || story.id}
                className={styles.card}
                style={{ cursor: story.sourceFilename ? 'pointer' : undefined }}
                onClick={e => {
                  if ((e.target as HTMLElement).closest('button, a')) return
                  if (story.sourceFilename) window.location.hash = `#stories/${encodeURIComponent(story.sourceFilename)}`
                }}
              >
                <div className={styles.cardHeader}>
                  <span className={styles.cardTitle}>{story.title || story.sourceFilename}</span>
                  {story.version && (
                    <span className={styles.versionBadge}>v{story.version}</span>
                  )}
                </div>

                {story.characterName && (
                  <span className={styles.characterPill}>
                    {story.characterName}{story.characterVersion > 1 ? ` v${story.characterVersion}` : ''}
                  </span>
                )}

                {story.id && (
                  <span className={styles.storyId}>{story.id}</span>
                )}

                {story.description && (
                  <p className={styles.description}>{story.description}</p>
                )}

                {exportErrors[story.sourceFilename] && (
                  <p className={styles.cardError}>Export failed: {exportErrors[story.sourceFilename]}</p>
                )}

                <div className={styles.cardFooter}>
                  <div className={styles.cardFooterLeft}>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => { setDeleteError(''); setConfirmDelete(story) }}
                      title="Remove from database"
                    >
                      Delete
                    </button>
                    {story.sourceFilename && (
                      <button
                        className={styles.exportBtn}
                        onClick={() => handleExport(story.sourceFilename)}
                        disabled={exporting.has(story.sourceFilename)}
                        title="Download this story as YAML"
                      >
                        {exporting.has(story.sourceFilename) ? 'Exporting…' : 'Export'}
                      </button>
                    )}
                  </div>
                  <div className={styles.cardFooterActions}>
                    {story.sourceFilename && (
                      <>
                        <a
                          className={`${styles.beginStoryBtn} ${styles.footerBtn}`}
                          href={`?story=${encodeURIComponent(story.sourceFilename)}&features=storyteller#chat`}
                          title="Start a Story session with this character in Storyteller mode"
                        >
                          Begin Story
                        </a>
                        <a
                          className={`${styles.startChatBtn} ${styles.footerBtn}`}
                          href={`?story=${encodeURIComponent(story.sourceFilename)}#chat`}
                          title="Start a Chat session with this character in default mode"
                        >
                          Start Chat
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null) }}>
          <div className={styles.modal} role="dialog" aria-modal="true">
            <h2>Delete story?</h2>
            <p>
              <strong>{confirmDelete.title || confirmDelete.sourceFilename}</strong> will be removed from the database.
            </p>
            {deleteError && <p className={styles.modalError}>{deleteError}</p>}
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setConfirmDelete(null)} disabled={deleting}>
                Cancel
              </button>
              <button className={styles.confirmDeleteBtn} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
